import { useState, type FormEvent } from "react";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { contactInfo } from "@/mocks/site";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db9l7t8kk00l6eltrt5g";

const positions = [
  {
    icon: "ri-moon-clear-line",
    title: "为什么做午夜视频",
    text: "深夜的片单和白天不一样。我们想做一个不催促你快点决定的地方，用编辑推荐和清晰分类，陪你把这一晚挑完。",
  },
  {
    icon: "ri-stack-line",
    title: "我们收录什么",
    text: "以午夜氛围为核心的剧情、悬疑、犯罪、恐怖与科幻电影，以及三分钟以内的剧情、生活、搞笑和剪辑类短视频。",
  },
  {
    icon: "ri-shield-check-line",
    title: "我们不做的事",
    text: "不提供影视播放与下载，不售卖任何商品，不使用真实演员信息。站内所有影片与短视频均为演示虚构内容。",
  },
];

const notices = [
  "本站所有影片海报、短视频封面、简介与人员信息均为演示用虚构内容，不对应任何真实作品或个人。",
  "站点当前不提供在线播放、下载或购买功能，仅展示封面与文字资料，用于内容浏览与发现。",
  "收藏功能仅将编号保存在本机浏览器，不会上传到服务器，清除浏览器数据后收藏会同步消失。",
  "如发现页面内容存在明显错漏，欢迎通过下方反馈表单告知，我们会在工作日 24 小时内查看。",
];

export default function About() {
  usePageMeta(
    "关于我们 - 午夜视频",
    "了解午夜视频的定位与内容原则，查看联系方式、意见反馈表单、使用须知与版权声明。本站仅展示影视封面与信息，不提供播放。",
    "关于午夜视频,午夜视频联系方式,影视网站反馈,午夜视频版权声明",
  );

  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [feedbackType, setFeedbackType] = useState("内容纠错");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(contactInfo.email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const honeyValue = String(formData.get("website_alt") ?? "").trim();

    if (honeyValue) {
      setStatus("success");
      return;
    }

    if (!nickname.trim() || !email.trim() || !message.trim()) {
      setStatus("error");
      setFormError("请把昵称、邮箱和反馈内容填写完整，方便我们回复你。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
        detail?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        parsed?.detail ||
        responseText;

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        setNickname("");
        setEmail("");
        setFeedbackType("内容纠错");
        setMessage("");
        formElement.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交没有成功，请稍后再试一次。");
      }
    } catch {
      setStatus("error");
      setFormError("网络似乎不太稳定，反馈没有送达。请检查网络后重试，或直接发邮件给我们。");
    }
  };

  return (
    <PageLayout>
      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        <div className="relative overflow-hidden rounded-lg border border-background-800 bg-primary-950 px-5 py-9 md:px-9 md:py-12">
          <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-accent-500/10 blur-3xl" />
          <div className="relative max-w-3xl">
            <p className="flex items-center gap-2 font-label text-xs tracking-[0.3em] text-accent-500">
              <span className="inline-block h-px w-6 bg-accent-500/70" />
              ABOUT MIDNIGHT
            </p>
            <h1 className="mt-3 font-heading text-2xl font-bold tracking-wide text-foreground-50 md:text-4xl">
              关于午夜视频
            </h1>
            <p className="mt-4 text-sm leading-7 text-foreground-300 md:text-base">
              午夜视频是一个专注深夜观影的内容发现平台。我们相信深夜看片是一种状态，而不是一个分类：它需要安静的排版、克制的配色，以及一份真正被编辑挑选过的片单。
            </p>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-xs text-foreground-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-mail-line text-sm text-accent-500" />
                {contactInfo.email}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm text-accent-500" />
                {contactInfo.hours}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-sm text-accent-500" />
                {contactInfo.address}
              </span>
            </div>
          </div>
        </div>

        <section className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          {positions.map((item) => (
            <article
              key={item.title}
              className="rounded-lg border border-background-800 bg-background-900/70 p-5"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-900 text-accent-500">
                <i className={`${item.icon} text-lg`} />
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-foreground-50">
                {item.title}
              </h2>
              <p className="mt-2 text-sm leading-7 text-foreground-400">{item.text}</p>
            </article>
          ))}
        </section>

        <section id="contact" className="mt-10 scroll-mt-24">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
            <div className="rounded-lg border border-background-800 bg-background-900/70 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-50">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                意见反馈
              </h2>
              <p className="mt-2 text-xs leading-6 text-foreground-500">
                内容纠错、影片推荐、合作咨询都可以在这里告诉我们。带 * 的字段为必填项。
              </p>

              <form
                id="feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="feedback-name" className="mb-1.5 block text-xs text-foreground-400">
                      昵称 *
                    </label>
                    <input
                      id="feedback-name"
                      name="name"
                      type="text"
                      value={nickname}
                      onChange={(event) => setNickname(event.target.value)}
                      placeholder="怎么称呼你"
                      required
                      className="h-11 w-full rounded-md border border-background-700 bg-background-950 px-3 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-accent-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="feedback-email" className="mb-1.5 block text-xs text-foreground-400">
                      邮箱 *
                    </label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="用于接收回复"
                      required
                      className="h-11 w-full rounded-md border border-background-700 bg-background-950 px-3 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-accent-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback-type" className="mb-1.5 block text-xs text-foreground-400">
                    反馈类型
                  </label>
                  <div className="relative">
                    <select
                      id="feedback-type"
                      name="type"
                      value={feedbackType}
                      onChange={(event) => setFeedbackType(event.target.value)}
                      className="h-11 w-full appearance-none rounded-md border border-background-700 bg-background-950 px-3 pr-9 text-sm text-foreground-100 outline-none transition-colors focus:border-accent-500"
                    >
                      <option value="内容纠错">内容纠错</option>
                      <option value="影片推荐">影片推荐</option>
                      <option value="功能建议">功能建议</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他">其他</option>
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback-message" className="mb-1.5 block text-xs text-foreground-400">
                    反馈内容 *
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    value={message}
                    onChange={(event) => setMessage(event.target.value.slice(0, 500))}
                    maxLength={500}
                    rows={5}
                    required
                    placeholder="请描述你遇到的问题或建议，最多 500 字"
                    className="w-full resize-none rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm leading-6 text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-accent-500"
                  />
                  <p className="mt-1 text-right text-[11px] text-foreground-600">
                    {message.length} / 500
                  </p>
                </div>

                <div className="feedback-shuttle-field" aria-hidden="true">
                  <label htmlFor="feedback-website-alt">网站地址</label>
                  <input
                    id="feedback-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-6 py-3 text-sm font-medium transition-colors ${
                      status === "submitting"
                        ? "cursor-not-allowed bg-background-700 text-foreground-400"
                        : "bg-accent-500 text-background-950 hover:bg-accent-400"
                    }`}
                  >
                    <i className={`${status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} text-base`} />
                    {status === "submitting" ? "正在提交…" : "提交反馈"}
                  </button>
                  <p className="text-[11px] leading-5 text-foreground-500">
                    提交后我们会在 {contactInfo.response}，请留意邮箱。
                  </p>
                </div>

                {status === "success" && (
                  <p className="flex items-start gap-2 rounded-md border border-accent-500/30 bg-primary-950 px-3 py-2.5 text-xs leading-6 text-accent-400">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base" />
                    反馈已经收到，谢谢你帮我们把内容做得更好。我们会尽快查看并回复。
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-start gap-2 rounded-md border border-accent-700/50 bg-background-900 px-3 py-2.5 text-xs leading-6 text-accent-300">
                    <i className="ri-error-warning-line mt-0.5 text-base" />
                    {formError}
                  </p>
                )}
              </form>
            </div>

            <aside className="flex flex-col gap-4">
              <div className="rounded-lg border border-background-800 bg-background-900/70 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-50">
                  <i className="ri-mail-send-line text-base text-accent-500" />
                  直接联系我们
                </h2>
                <p className="mt-2 text-xs leading-6 text-foreground-400">
                  如果是内容授权或媒体合作，建议直接发邮件，处理会更快。
                </p>
                <div className="mt-4 rounded-md border border-background-700 bg-background-950 px-3 py-3">
                  <p className="text-[11px] text-foreground-500">官方邮箱</p>
                  <p className="mt-1 break-all text-sm text-foreground-100">{contactInfo.email}</p>
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="mt-3 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-700 px-2.5 py-1.5 text-xs text-foreground-300 transition-colors hover:border-accent-500/60 hover:text-accent-400"
                  >
                    <i className="ri-file-copy-line text-sm" />
                    {copied ? "已复制" : "复制邮箱"}
                  </button>
                </div>
                <ul className="mt-4 space-y-2.5 text-xs text-foreground-400">
                  <li className="flex items-center justify-between gap-2">
                    <span className="text-foreground-500">回复时间</span>
                    <span>{contactInfo.response}</span>
                  </li>
                  <li className="flex items-center justify-between gap-2">
                    <span className="text-foreground-500">在线时段</span>
                    <span>{contactInfo.hours}</span>
                  </li>
                  <li className="flex items-center justify-between gap-2">
                    <span className="text-foreground-500">运营方式</span>
                    <span>{contactInfo.address}</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border border-background-800 bg-primary-950 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-100">
                  <i className="ri-question-answer-line text-base text-accent-500" />
                  常见问题
                </h2>
                <dl className="mt-3 space-y-3 text-xs leading-6">
                  <div>
                    <dt className="text-foreground-200">为什么点播放没有反应？</dt>
                    <dd className="mt-1 text-foreground-500">
                      站点尚未接入播放能力，页面只展示封面与资料，属于正常情况。
                    </dd>
                  </div>
                  <div>
                    <dt className="text-foreground-200">收藏会同步到别的设备吗？</dt>
                    <dd className="mt-1 text-foreground-500">
                      不会。收藏保存在当前浏览器本地，仅作功能演示。
                    </dd>
                  </div>
                </dl>
              </div>
            </aside>
          </div>
        </section>

        <section id="notice" className="mt-10 scroll-mt-24">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-50">
            <span className="h-4 w-1 rounded-full bg-accent-500" />
            使用须知
          </h2>
          <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {notices.map((item) => (
              <li
                key={item}
                className="flex gap-2.5 rounded-lg border border-background-800 bg-background-900/60 px-4 py-3.5 text-xs leading-6 text-foreground-400"
              >
                <i className="ri-checkbox-blank-circle-line mt-1.5 shrink-0 text-[10px] text-accent-500" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section id="copyright" className="mt-10 scroll-mt-24">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-50">
            <span className="h-4 w-1 rounded-full bg-accent-500" />
            版权声明
          </h2>
          <div className="mt-4 rounded-lg border border-background-800 bg-background-900/60 p-5 text-xs leading-7 text-foreground-400">
            <p>
              本站为影视内容展示型演示站点，站内影片名称、人物、简介、封面与短视频内容均为虚构或由程序生成，仅用于产品形态展示，不构成任何作品推荐或商业宣传。
            </p>
            <p className="mt-3">
              站点不存储、不转码、不分发任何影视作品，也不提供播放与下载入口。如相关权利方认为页面内容存在不妥，可通过上方邮箱联系我们，我们会在核实后第一时间处理。
            </p>
            <p className="mt-3 text-foreground-500">
              © 2025 午夜视频 Midnight Video. 保留所有页面设计与文字内容的展示权利。
            </p>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}